// hooks/useAsyncCursorLoader.js
import { useCallback, useRef } from "react";
import APIService from "../../Service/APIService";

/**
 * A generic loadOptions builder for react-select-async-paginate.
 * - Supports cursor pagination and prefix search.
 * - Works across package versions (thirdArg vs { additional }).
 */
export default function useAsyncCursorLoader({
  apiPath = "/GetReportFilterOptions",
  pageSize = 25,
  // query params sent every time (e.g., { type: 'accounts' })
  staticParams = {},
  // map a raw item to { value, label } — override if your API differs
  mapItem = (x) => ({
    ...x,
    value: x.ID ?? x.id,
    label: x.Name ?? x.AccountName ?? x.name,
  }),
}) {
  const lastQueryRef = useRef("");

  const loadOptions = useCallback(async (inputValue, _loaded, thirdArg) => {
    try {
      // Normalize "additional" (cursor) across lib versions
      const additional =
        thirdArg && typeof thirdArg === "object" && "additional" in thirdArg
          ? thirdArg.additional
          : thirdArg ?? null;

      const q = (inputValue || "").trim();
      const prevQ = lastQueryRef.current;

      // Reset cursor if the search text changed
      const cursor = q === prevQ ? additional ?? null : null;
      lastQueryRef.current = q;

      // Build params
      const params = new URLSearchParams();
      Object.entries(staticParams).forEach(([k, v]) => v != null && params.set(k, String(v)));
      if (q) params.set("q", q);
      if (cursor) params.set("cursor", cursor);
      params.set("limit", String(pageSize));

      const apiServe = new APIService();
      const resp = await apiServe.getDataFromBackend(`${apiPath}?${params.toString()}`);

      const data = resp?.data ?? resp ?? {};
      const items = data?.items ?? data ?? [];
      const nextCursor = data?.nextCursor ?? null;

      const options = (Array.isArray(items) ? items : []).map(mapItem).filter(o => o?.value != null && o?.label != null);

      return {
        options,
        hasMore: Boolean(nextCursor),
        additional: nextCursor,
      };
    } catch (err) {
      console.error("[useAsyncCursorLoader] loadOptions error", err);
      return { options: [], hasMore: false, additional: null };
    }
  }, [apiPath, pageSize, staticParams, mapItem]);

  return loadOptions;
}
